/**
 * Edit Portfolio Modal
 * TASK-210: Edit portfolio details
 * TASK-263: Updated to accept flexible portfolio type (works with API and mock data)
 * TASK-321: Added data source configuration for Pretim integration
 *
 * Allows editing portfolio name, custodian, account number, currency, status,
 * and data source configuration for external sync.
 */

import React, { useState, useEffect } from 'react';
import { X, Save, RefreshCw, Database, Upload, Info } from 'lucide-react';
import { MockPortfolio } from '../../services/mock/portfolioMockData';
import { toast } from 'sonner';
import SyncStatusBadge from './atoms/SyncStatusBadge';
import SyncHistoryPanel from './SyncHistoryPanel';
import { portfolioApiService } from '../../services/api/portfolioApiService';

/**
 * TASK-263/TASK-321: Flexible portfolio type for editing
 * Works with both MockPortfolio and EnrichedPortfolio, includes sync fields
 */
interface EditablePortfolio {
  id?: string;
  clientId?: string;
  portfolioName: string;
  portfolioType: 'manual' | 'api';
  custodian?: string;
  accountNumber?: string;
  currencyBase?: string;
  currency?: string;  // Fallback for currencyBase
  status: 'active' | 'closed' | 'archived';
  // TASK-321: Sync configuration fields
  dataSource?: 'manual' | 'pretim' | 'csv_import';
  externalId?: string;
  syncEnabled?: boolean;
  syncFrequency?: 'realtime' | 'hourly' | 'daily' | 'weekly' | 'manual';
  lastSyncAt?: string;
  lastSyncStatus?: 'success' | 'partial' | 'failed' | 'pending';
}

interface EditPortfolioModalProps {
  isOpen: boolean;
  portfolio: EditablePortfolio;
  onClose: () => void;
  onSave: (updatedPortfolio: Partial<MockPortfolio>) => void;
}

// Type definitions for form data
// Note: Some types differ from MockPortfolio for Pretim integration (TASK-321)
type DataSourceType = 'manual' | 'pretim' | 'csv_import';
type SyncFrequencyType = 'realtime' | 'hourly' | 'daily' | 'weekly' | 'manual';
type PortfolioStatusType = 'active' | 'closed' | 'archived';
type CurrencyType = 'CHF' | 'EUR' | 'USD';
type CustodianType = 'UBS' | 'Credit Suisse' | 'Julius Baer' | 'Pictet' | 'Vontobel' | 'None';

interface FormDataState {
  portfolioName: string;
  custodian: CustodianType;
  accountNumber: string;
  currencyBase: CurrencyType;
  status: PortfolioStatusType;
  dataSource: DataSourceType;
  externalId: string;
  syncEnabled: boolean;
  syncFrequency: SyncFrequencyType;
}

const EditPortfolioModal: React.FC<EditPortfolioModalProps> = ({
  isOpen,
  portfolio,
  onClose,
  onSave
}) => {
  // Helper to create initial form state from portfolio
  const createInitialFormState = (): FormDataState => ({
    portfolioName: portfolio.portfolioName,
    custodian: (portfolio.custodian || 'None') as CustodianType,
    accountNumber: portfolio.accountNumber || '',
    currencyBase: (portfolio.currencyBase || portfolio.currency || 'CHF') as CurrencyType,
    status: portfolio.status,
    // TASK-321: Sync configuration
    dataSource: (portfolio.dataSource || 'manual') as DataSourceType,
    externalId: portfolio.externalId || '',
    syncEnabled: portfolio.syncEnabled || false,
    syncFrequency: (portfolio.syncFrequency || 'daily') as SyncFrequencyType
  });

  const [formData, setFormData] = useState<FormDataState>(createInitialFormState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSavingConfig, setIsSavingConfig] = useState(false);

  // Reset form state when portfolio changes (e.g., modal reopens with different portfolio)
  useEffect(() => {
    setFormData({
      portfolioName: portfolio.portfolioName,
      custodian: (portfolio.custodian || 'None') as CustodianType,
      accountNumber: portfolio.accountNumber || '',
      currencyBase: (portfolio.currencyBase || portfolio.currency || 'CHF') as CurrencyType,
      status: portfolio.status,
      dataSource: (portfolio.dataSource || 'manual') as DataSourceType,
      externalId: portfolio.externalId || '',
      syncEnabled: portfolio.syncEnabled || false,
      syncFrequency: (portfolio.syncFrequency || 'daily') as SyncFrequencyType
    });
    setErrors({});
  }, [portfolio]);

  const custodians = [
    'UBS',
    'Credit Suisse',
    'Julius Baer',
    'Pictet',
    'Vontobel',
    'None'
  ];

  const handleChange = (field: keyof FormDataState, value: FormDataState[keyof FormDataState]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.portfolioName.trim()) {
      newErrors.portfolioName = 'Portfolio name is required';
    }

    // TASK-321: Require external ID when using Pretim
    if (formData.dataSource === 'pretim' && !formData.externalId.trim()) {
      newErrors.externalId = 'Pretim Portfolio ID is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    // TASK-321: Save sync configuration if portfolio has ID
    if (portfolio.id && (
      formData.dataSource !== portfolio.dataSource ||
      formData.externalId !== portfolio.externalId ||
      formData.syncEnabled !== portfolio.syncEnabled ||
      formData.syncFrequency !== portfolio.syncFrequency
    )) {
      setIsSavingConfig(true);
      try {
        await portfolioApiService.updateSyncConfig(portfolio.id, {
          data_source: formData.dataSource,
          external_id: formData.externalId || undefined,
          sync_enabled: formData.syncEnabled,
          sync_frequency: formData.syncFrequency
        });
      } catch {
        toast.error('Failed to update sync settings');
        setIsSavingConfig(false);
        return;
      }
      setIsSavingConfig(false);
    }

    // Map syncFrequency to MockPortfolio compatible type
    // 'realtime' and 'hourly' are Pretim-specific, map to 'daily' for mock storage
    const mockSyncFrequency = (['daily', 'weekly', 'manual'] as const).includes(
      formData.syncFrequency as 'daily' | 'weekly' | 'manual'
    ) ? formData.syncFrequency as 'daily' | 'weekly' | 'manual' : 'daily';

    onSave({
      portfolioName: formData.portfolioName,
      custodian: formData.custodian,
      accountNumber: formData.accountNumber,
      currencyBase: formData.currencyBase,
      status: formData.status,
      syncEnabled: formData.syncEnabled,
      syncFrequency: mockSyncFrequency,
      updatedAt: new Date().toISOString()
    });

    toast.success('Portfolio updated successfully!');
    onClose();
  };

  // Data source options
  const dataSources = [
    { value: 'manual', label: 'Manual', icon: Database, description: 'Manually managed positions' },
    { value: 'pretim', label: 'Pretim API', icon: RefreshCw, description: 'Sync from Pretim' },
    { value: 'csv_import', label: 'CSV Import', icon: Upload, description: 'Import from CSV file' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Edit Portfolio</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {portfolio.portfolioType === 'api' ? 'API-Connected' : 'Manual'} Portfolio
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="space-y-4">
            {/* Portfolio Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Portfolio Name *
              </label>
              <input
                type="text"
                value={formData.portfolioName}
                onChange={(e) => handleChange('portfolioName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.portfolioName ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., UBS Main Account"
              />
              {errors.portfolioName && (
                <p className="mt-1 text-sm text-red-600">{errors.portfolioName}</p>
              )}
            </div>

            {/* Custodian */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custodian
              </label>
              <select
                value={formData.custodian}
                onChange={(e) => handleChange('custodian', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {custodians.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {/* Account Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Number
              </label>
              <input
                type="text"
                value={formData.accountNumber}
                onChange={(e) => handleChange('accountNumber', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., CH-123456"
              />
            </div>

            {/* Base Currency */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Base Currency
              </label>
              <select
                value={formData.currencyBase}
                onChange={(e) => handleChange('currencyBase', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="CHF">CHF - Swiss Franc</option>
                <option value="EUR">EUR - Euro</option>
                <option value="USD">USD - US Dollar</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Portfolio Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="active">Active</option>
                <option value="closed">Closed</option>
                <option value="archived">Archived</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Closed portfolios won't show in active views
              </p>
            </div>

            {/* TASK-321: Data Source Configuration */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">
                  Data Source
                </h3>
                {/* Current sync status badge */}
                {portfolio.dataSource && portfolio.dataSource !== 'manual' && (
                  <SyncStatusBadge
                    status={portfolio.lastSyncStatus || 'never'}
                    dataSource={portfolio.dataSource}
                    lastSyncAt={portfolio.lastSyncAt}
                    syncEnabled={portfolio.syncEnabled}
                    showDetails
                    size="sm"
                  />
                )}
              </div>

              {/* Data Source Dropdown */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Source Type
                </label>
                <select
                  value={formData.dataSource}
                  onChange={(e) => handleChange('dataSource', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {dataSources.map(source => (
                    <option key={source.value} value={source.value}>
                      {source.label} - {source.description}
                    </option>
                  ))}
                </select>
              </div>

              {/* External Portfolio ID (shown for Pretim) */}
              {formData.dataSource === 'pretim' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pretim Portfolio ID *
                  </label>
                  <input
                    type="text"
                    value={formData.externalId}
                    onChange={(e) => handleChange('externalId', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.externalId ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="e.g., portfolio-uuid-from-pretim"
                  />
                  {errors.externalId && (
                    <p className="mt-1 text-sm text-red-600">{errors.externalId}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    The portfolio ID from your Pretim account
                  </p>
                </div>
              )}

              {/* Sync Enabled Toggle (for non-manual sources) */}
              {formData.dataSource !== 'manual' && (
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Auto-sync Enabled
                    </label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Automatically sync positions on schedule
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleChange('syncEnabled', !formData.syncEnabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.syncEnabled ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.syncEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              )}

              {/* Sync Frequency (when sync is enabled) */}
              {formData.dataSource !== 'manual' && formData.syncEnabled && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sync Frequency
                  </label>
                  <select
                    value={formData.syncFrequency}
                    onChange={(e) => handleChange('syncFrequency', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="realtime">Real-time</option>
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="manual">Manual only</option>
                  </select>
                </div>
              )}

              {/* Sync History Panel (for portfolios with ID and non-manual source) */}
              {portfolio.id && portfolio.clientId && formData.dataSource !== 'manual' && (
                <SyncHistoryPanel
                  portfolioId={portfolio.id}
                  clientId={portfolio.clientId}
                  externalPortfolioId={formData.externalId}
                  dataSource={formData.dataSource}
                  syncEnabled={formData.syncEnabled}
                  className="mt-4"
                />
              )}
            </div>

            {/* Info box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-700">
                  <p className="font-medium text-blue-900 mb-1">Note</p>
                  <p>
                    Changing portfolio settings won't affect existing positions or holdings.
                    {formData.dataSource !== 'manual' && ' Disabling sync will stop automatic updates but keep existing data.'}
                  </p>
                  {formData.dataSource === 'pretim' && !formData.externalId && (
                    <p className="mt-1 text-yellow-700">
                      Enter your Pretim Portfolio ID to enable syncing.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <button
            onClick={onClose}
            disabled={isSavingConfig}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            disabled={isSavingConfig}
            className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
          >
            {isSavingConfig ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isSavingConfig ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditPortfolioModal;
